import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  getCurrentSlice,
  getQueryString,
  magicNumbers,
  platformToReducerName,
} from 'utils';
import {
  store,
  setCurrentQuery,
  setCurrentDate,
  setQueries,
  setPlatformCurrentPageNumber,
  applyFiltersToApiRequest,
  applyFiltersToAllPlatforms,
} from 'store';
import usePermission from './usePermission';
import usePlatform from './usePlatform';

const mapPlatformToReducer = (platform) => {
  return platformToReducerName[platform];
};

const apiPathToReducerName = {
  instagramusers: 'instagramContentReducer',
  newsagencies: 'newsContentReducer',
  newspaperagencies: 'newspaperContentReducer',
  telegramchannels: 'telegramContentReducer',
  twitterusers: 'twitterContentReducer',
  linkedinusers: 'linkedinContentReducer',
  'iranian-platforms-channels': 'nativePlatformsContentReducer',
  instagramCommentByPost: 'instagramCommentByPostContentReducer',
  instagramComment: 'instagramCommentByCommentContentReducer',
};

const wordPhraseAllowedReducers = [
  'newsContentReducer',
  'instagramContentReducer',
  'instagramCommentByCommentContentReducer',
  'instagramCommentByPostContentReducer',
  'twitterContentReducer',
  'telegramContentReducer',
  'linkedinContentReducer',
  'nativePlatformsContentReducer',
];

const sentimentAllowedReducers = [
  'newsContentReducer',
  'instagramContentReducer',
  'instagramCommentByCommentContentReducer',
  'instagramCommentByPostContentReducer',
  'twitterContentReducer',
  'telegramContentReducer',
  'linkedinContentReducer',
  'nativePlatformsContentReducer',
];

export const useQueryStringFilters = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { userPermission } = usePermission();
  const { getSettingsDefaultPlatform } = usePlatform();
  const queryString = getQueryString();
  const { platform } = useParams();
  const currentReducerName = mapPlatformToReducer(platform);
  const { permission: hasFreeSearchAccess } = userPermission(
    'FrontEnd',
    'freeSearch'
  );
  const isWebsiteSettingsSynced = useSelector(
    (state) => state.settingsReducer.isWebsiteSettingsSynced
  );

  /**
   * A `q` param in the URL is a free-text ("free search") query composed
   * elsewhere (e.g. the AI assistant that builds these deep links). Apply it
   * exactly as the in-app search box does: switch the selected query to the
   * free-search sentinel, carry the text in `q`, and expose it in the query
   * picker so the search input shows it. This overrides whatever subject was
   * selected before. Gated behind the free-search permission, like every other
   * entry point to this mode.
   */
  const applyFreeSearchFromUrl = () => {
    const freeSearchText = queryString?.q;
    if (!freeSearchText || !hasFreeSearchAccess) return;
    if (!window.location.pathname.includes('contents')) return;

    const { queries, currentQuery } = store.getState().pageReducer;
    if (
      currentQuery.queryId === magicNumbers.YOUR_SEARCH_QUERY_ID &&
      currentQuery.q === freeSearchText
    ) {
      return;
    }

    const activePlatform = platform || getSettingsDefaultPlatform();
    const nextQueries = (queries.data || []).filter(
      (item) => item?.id !== magicNumbers.YOUR_SEARCH_QUERY_ID
    );
    nextQueries.unshift({
      id: magicNumbers.YOUR_SEARCH_QUERY_ID,
      parsed_query: freeSearchText,
      title: t('common:search.yourSearch'),
    });

    dispatch(
      setPlatformCurrentPageNumber({ platform: activePlatform, pageNumber: 1 })
    );
    dispatch(
      setCurrentQuery({
        lastQueryId: currentQuery.queryId,
        q: freeSearchText,
        lastSearchQuery: freeSearchText,
        queryId: magicNumbers.YOUR_SEARCH_QUERY_ID,
        freeSearchFallbackQueryId: 0,
      })
    );
    dispatch(setQueries(nextQueries));
    dispatch(applyFiltersToApiRequest());
    dispatch(applyFiltersToAllPlatforms());
  };

  const resetAllFilters = () => {
    Object.values(apiPathToReducerName).forEach((_reducerName) => {
      const { resetAllFilters } = getCurrentSlice(_reducerName);

      if (typeof resetAllFilters === 'function') {
        dispatch(resetAllFilters());
      }
    });
  };

  const dispatchSelectedIdsToAllContentSlices = (selectedTagIdsFromUrl) => {
    Object.values(apiPathToReducerName).forEach((_reducerName) => {
      const { setSelectedFilterIds, filtersInitialState } =
        getCurrentSlice(_reducerName);

      const hasContentTagFilter = Object.keys(
        filtersInitialState || {}
      ).includes('contentTagFilter');

      if (typeof setSelectedFilterIds === 'function' && hasContentTagFilter) {
        dispatch(
          setSelectedFilterIds({
            idsFromUrl: selectedTagIdsFromUrl,
            componentName: 'contentTagFilter',
          })
        );
      }
    });
  };

  /**
   * Waits for the website settings to be applied. fetchWebsiteSettings writes
   * the user's default subject, date range and per-platform filters into the
   * same slices this effect writes, and it resolves after mount, so applying
   * the URL first would let those defaults overwrite it. The content requests
   * are gated on the same flag, so they still go out with the URL values.
   */
  useEffect(() => {
    if (!isWebsiteSettingsSynced) return;

    if (queryString?.query_id) {
      resetAllFilters();
      dispatch(setCurrentQuery({ queryId: +queryString.query_id }));
    }

    applyFreeSearchFromUrl();

    if (
      queryString?.time_duration ||
      (queryString?.from_date && queryString?.to_date)
    ) {
      if (queryString.time_duration) {
        dispatch(
          setCurrentDate({
            fromDate: undefined,
            toDate: undefined,
            timeDuration: queryString.time_duration,
          })
        );
      } else {
        dispatch(
          setCurrentDate({
            fromDate: queryString.from_date ?? undefined,
            toDate: queryString.to_date ?? undefined,
            timeDuration: undefined,
          })
        );
      }
    }

    if (
      queryString?.hashtag_phrase &&
      window.location.pathname.includes('contents')
    ) {
      const _reducerName = store.getState().filterReducer.filtersContext;
      const { setSelectedFilterIds } = getCurrentSlice(_reducerName);
      dispatch(
        setSelectedFilterIds({
          idsFromUrl: [queryString?.hashtag_phrase],
          componentName: 'hashtagCloudFilter',
        })
      );
    }
    if (
      queryString?.word_phrase &&
      window.location.pathname.includes('contents')
    ) {
      wordPhraseAllowedReducers.forEach((_reducerName) => {
        const { setSelectedFilterIds } = getCurrentSlice(_reducerName);
        if (typeof setSelectedFilterIds === 'function') {
          dispatch(
            setSelectedFilterIds({
              idsFromUrl: [queryString?.word_phrase],
              componentName: 'wordCloudFilter',
            })
          );
        }
      });
    }
    if (queryString?.content_tag) {
      const selectedTagCategoryFromUrl = JSON.parse(
        localStorage.getItem(`selectedFilterTag_${queryString?.content_tag}`)
      );
      const selectedTagIdsFromUrl = selectedTagCategoryFromUrl?.tags.map(
        (tag) => tag.id
      );
      dispatchSelectedIdsToAllContentSlices(selectedTagIdsFromUrl);
    }

    if (queryString?.resource_category) {
      const selectedResourceCategoryFromUrl = JSON.parse(
        localStorage.getItem(
          `selectedFilterResourceCategory_${queryString?.resource_category}`
        )
      );
      Object.keys(selectedResourceCategoryFromUrl.platforms || []).forEach(
        (platform) => {
          const { setSelectedFilterValue } = getCurrentSlice(
            apiPathToReducerName[platform]
          );
          if (typeof setSelectedFilterValue === 'function') {
            dispatch(
              setSelectedFilterValue({
                value: selectedResourceCategoryFromUrl.id,
                isFromUrl: true,
                componentName: 'resourceCategoryFilter',
              })
            );
          }
        }
      );
    }

    if (queryString?.resource_id) {
      const { setSelectedItems } = getCurrentSlice(currentReducerName);
      if (typeof setSelectedItems === 'function') {
        dispatch(
          setSelectedItems({
            item: {
              id: queryString.resource_id,
              title: queryString.resource_name?.replaceAll('_', ' '),
            },
            isFromUrl: true,
            isChecked: true,
            componentName: 'resourcesFilter',
          })
        );
      }
    }

    if (queryString?.sentiment) {
      sentimentAllowedReducers.forEach((_reducerName) => {
        const { setSelectedSentimentValues } = getCurrentSlice(_reducerName);
        if (typeof setSelectedSentimentValues === 'function') {
          dispatch(
            setSelectedSentimentValues({
              id: queryString.sentiment,
              value: true,
              componentName: 'sentimentFilter',
              parentPlatform: undefined,
            })
          );
        }
      });
    }
  }, [
    dispatch,
    queryString?.content_tag,
    queryString?.resource_category,
    queryString.resource_id,
    queryString.resource_name,
    queryString?.hashtag_phrase,
    queryString?.word_phrase,
    queryString?.from_date,
    queryString?.to_date,
    queryString?.time_duration,
    queryString?.query_id,
    queryString?.sentiment,
    queryString?.q,
    hasFreeSearchAccess,
    isWebsiteSettingsSynced,
  ]);
};
